"use client";

import {useState} from "react";
import Button from "../Button";
import {ButtonSizes, ButtonVariant} from "../ButtonVariants";
import TextField from "../forms/TextField";
import Input from "../forms/Input";
import useInput from "../forms/useInput";
import {useClientDeviceId} from "../contexts/ClientSourceTrackingContext";
import joinMailingList from "@/lib/legacyApi/joinMailingList";
import {Form, InputContext, useSlottedContext} from "react-aria-components";
import {ERROR_CODES} from "@/shared/util/ErrorCodes";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheckCircle} from "@fortawesome/sharp-solid-svg-icons/faCheckCircle";
import {useScreenWidth} from "@/app/util/useScreenWidth";
import {BREAKPOINT_MAP} from "@/app/util/tailwindUtils";
import clsMerge from "@/app/util/clsMerge";

/**
 * We may want to reuse this kind of style later but for now it's a one-off
 * with some mildly weird behaviour built into it so I'm just leaving it here.
 */
const FooterEmailInputLgBreakpoint = ({
	isSubmitting,
}: {
	isSubmitting: boolean;
}) => {
	const inputContext = useSlottedContext(InputContext) ?? {};
	const {"aria-invalid": isInvalid, disabled: isDisabled} = inputContext;
	const screenWidth = useScreenWidth();

	return (
		<div
			className={clsMerge(
				"hidden w-full flex-row items-center gap-2 rounded-md border border-solid border-gray-400 py-2 pr-2 lg:flex [&:has(:focus-visible)]:border-navy-600 [&:has(:focus-visible)]:bg-white [&:has(:focus-visible)]:ring [&:has(:focus-visible)]:ring-navy-200",
				!isInvalid && !isDisabled && "bg-white",
				isInvalid && "border-semantic-error-300 bg-semantic-error-100",
				(isDisabled || isSubmitting) &&
					"border-gray-500 bg-gray-200 text-gray-700"
			)}
			aria-hidden={screenWidth < BREAKPOINT_MAP.lg}
		>
			<Input
				placeholder="Enter your email"
				className="h-10 w-full bg-transparent focus-visible:outline-none"
				skipCustomStyles={screenWidth >= BREAKPOINT_MAP.lg}
			/>
			<Button
				className="w-36 max-w-full"
				size={ButtonSizes.SMALL}
				variant={ButtonVariant.SECONDARY}
				type="submit"
				disabled={isSubmitting}
			>
				{isSubmitting ? "..." : "Subscribe"}
			</Button>
		</div>
	);
};

const FooterEmailInput = ({isSubmitting}: {isSubmitting: boolean}) => {
	const screenWidth = useScreenWidth();
	return (
		<div
			className="flex flex-col items-start gap-2 lg:hidden"
			aria-hidden={screenWidth >= BREAKPOINT_MAP.lg}
		>
			<Input placeholder="Enter your email" />
			<Button
				size={ButtonSizes.SMALL}
				variant={ButtonVariant.SECONDARY}
				type="submit"
				disabled={isSubmitting}
			>
				{isSubmitting ? "..." : "Subscribe"}
			</Button>
		</div>
	);
};

const FooterEmailForm = ({text}: {text: string}) => {
	const cid = useClientDeviceId();
	const [email, handlers] = useInput();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [hasSuccessfullySubmitted, setHasSuccessfullySubmitted] =
		useState(false);
	const [submittedEmail, setSubmittedEmail] = useState<string>();
	const [errors, setErrors] = useState<Record<string, string>>();

	const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		setIsSubmitting(true);
		setSubmittedEmail(email);
		setHasSuccessfullySubmitted(false);
		// TODO: For now always join national mailing list from the footer
		//       in the future we might do something more involved to make sure
		//       people only sign up for campuses of interest
		const result = await joinMailingList({
			email,
			campus_id: "national",
			client_device_id: cid,
		});
		if (result.success) {
			setErrors(undefined);
			setHasSuccessfullySubmitted(true);
		}

		if (result.code === ERROR_CODES.E_INVALID_FIELDS) {
			setErrors(result.moreInfo);
		}

		setIsSubmitting(false);
	};

	return (
		<div className="flex flex-col gap-2">
			<p>{text}</p>
			<div className="flex flex-col gap-4 lg:gap-0.5">
				<Form
					className="relative flex flex-col items-start gap-4"
					onSubmit={onSubmit}
					validationErrors={errors}
				>
					<TextField
						className="w-full max-w-md lg:max-w-none"
						type="email"
						name="email"
						value={email}
						onInput={handlers.onInputChange}
						isDisabled={isSubmitting}
						autoComplete="email"
						hideLabelDecorators
						labelText="Email"
						hideLabel
					>
						<FooterEmailInput isSubmitting={isSubmitting} />
						<FooterEmailInputLgBreakpoint isSubmitting={isSubmitting} />
					</TextField>
				</Form>
				{hasSuccessfullySubmitted && (
					<p className="flex pl-0.5 text-sm lg:pl-0">
						<FontAwesomeIcon
							className="mr-2 text-lg text-green-600"
							icon={faCheckCircle}
						/>
						{submittedEmail} has been added to the mailing list!
					</p>
				)}
			</div>
		</div>
	);
};

export default FooterEmailForm;
